/**
 * D20 (mig 795) — as reposições de material à Route, na tela de Orçamentos da Manutenção (uma aba, ❌ não uma tela nova).
 * Nasce no check-out de cliente da Route quando o técnico usou material da IGM. 🔴 O cliente nunca recebe. A Manutenção
 * marca "reposta" quando a Route repõe.
 */
import { useEffect, useState } from 'react';

import { Badge, Button, EmptyState } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { usePodeAcessar } from '@/lib/acesso-store';

interface Reposicao {
  id: string;
  osNumero: number;
  clienteNome: string;
  itens: Array<{ codigo: string; descricao: string; quantidade: number }>;
  status: 'pendente' | 'reposta';
  criadoEmIso: string;
  repostaPor: string | null;
  repostaEmIso: string | null;
}

const B = '/api/v1/os/reposicoes-route';

export function ReposicoesDaRoute() {
  const podeMarcar = usePodeAcessar('manutencao.os.gerir');
  const [lista, setLista] = useState<Reposicao[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    Promise.resolve()
      .then(() => api.get<Reposicao[]>(B))
      .then((r) => vivo && setLista(Array.isArray(r) ? r : []))
      .catch(() => vivo && setErro('Não deu para abrir as reposições.'));
    return () => {
      vivo = false;
    };
  }, []);

  async function marcar(id: string) {
    setErro(null);
    try {
      setLista(await api.post<Reposicao[]>(`${B}/${encodeURIComponent(id)}/reposta`, {}));
    } catch (e) {
      setErro(e instanceof ApiError ? ((e.response as { mensagem?: string } | undefined)?.mensagem ?? e.message) : 'Não deu para marcar.');
    }
  }

  if (!lista) return erro ? <p role="alert">{erro}</p> : <p className="text-sm text-ink-3">Abrindo…</p>;
  if (lista.length === 0) return <EmptyState title="Nenhuma reposição à Route" description="Aparece aqui quando o técnico usa material da IGM num cliente da Route." />;

  return (
    <div className="flex flex-col gap-2" data-testid="reposicoes-da-route">
      {erro ? (
        <p role="alert" className="text-sm font-semibold text-red-deep dark:text-red-glow">
          {erro}
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {lista.map((r) => (
          <li key={r.id} className="flex flex-wrap items-start gap-3 rounded-lg border border-line bg-paper-surface p-3 text-sm dark:border-line-dark dark:bg-paper-dark-surface">
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 font-semibold">
                OS #{r.osNumero} · {r.clienteNome}
                <Badge variant={r.status === 'reposta' ? 'success' : 'warning'}>{r.status === 'reposta' ? 'Reposta' : 'Esperando a Route'}</Badge>
              </p>
              <p className="text-ink-2 dark:text-ink-dark-2">{r.itens.map((i) => `${String(i.quantidade).replace('.', ',')} × ${i.descricao}`).join(' · ')}</p>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Pedida em {formatDateTime(r.criadoEmIso)}
                {r.repostaEmIso ? ` · reposta por ${r.repostaPor ?? 'alguém'} em ${formatDateTime(r.repostaEmIso)}` : ''}
              </p>
            </div>
            {r.status === 'pendente' && podeMarcar ? (
              <Button size="sm" variant="secondary" className="min-h-[44px] md:min-h-0" onClick={() => void marcar(r.id)}>
                Marcar reposta
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
